import { toast } from "react-toastify"
import React, { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router"

import { IDAM } from "../../api/Registry"
import { APPLICATION } from "../../utils/Constants"
import HttpServices from "../../services/HttpServices"
import { AttributeIdentity } from "./stages/AttributeIdentity"
import { TwoToneModal } from "../../lib/fragments/TwoToneModal"
import { AttributeOversight } from "./stages/AttributeOversight"
import { RouteHydration, getStandardRoute } from "../../utils/Helpers"
import { AttributeConfiguration } from "./stages/AttributeConfiguration"
import { G_onInputBlurHandler, G_onInputChangeHandler } from "../../components/utils/InputHandlers"
import { CreateOrEditAttributeGroup } from "../users.attributes/CreateOrEditAttributeGroup"

export const CreateAttribute = ({ show, showOrHide }) => {
    const [state, setstate] = useState({
        posting: false,
        httpStatus: 200,
        status: 'pending',
        stage: 'basic',
        data: {
            groups: null
        },
        input: {
            // Attribute Identity
            name: '',
            attr_group: null,
            hint: '',

            configuration: '',
            format: null,
            mandatory: false,
            minimum: '',
            maximum: '',
            unit_label: '',
            multiline: false,
            whole_numbers: false,
            allow_negative: false,
            allow_special: false,
            allow_mulitple: false,
            display_style: {
                key: "radio",
                label: "Radio Buttons",
            },
            text_case: {
                key: "mixed",
                label: "Mixed Case",
            },

            oversight: '',
            ownership: false,
            coowners: false,
            restricted: false,
            editable: false,
        },
        errors: {
            // Attribute Identity
            basic: '',
            name: '',
            attr_group: '',
            hint: '',

            // Attribute Configuration
            configuration: '',
            format: '',
            minimum: '',
            maximum: '',
            unit_label: '',
            display_style: '',

            // Oversight
            oversight: '',
        },
        show: {
            minMaxVisibility: false,
            createAttributeGroup: false,
        },
        cargo: {
            uuid: null,
            action: 'create'
        }
    })

    const minLength = 3;
    const maxLength = 200;

    const location = useLocation()
    const navigate = useNavigate()
    type ModalKeys = 'createAttributeGroup' | ''

    const formSections: Record<string, string[]> = {
        'basic': ["attr_group", "name", "hint"],
        'configuration': ["format", "display_style", "minimum", "maximum", "unit_label"],
    }

    const onCloseModal = async () => {
        showOrHide()

        // Reset the state on close
        setTimeout(() => {
            setstate((prev: any) => ({
                ...prev,
                posting: false,
                httpStatus: 200,
                stage: 'basic',
                status: 'pending',
                data: {
                    group: null
                },
            }))
        }, 300)
    }

    useEffect(() => {
        if (show) {
            getAttributeGroups()
        }
    }, [show])

    const getAttributeGroups = async () => {
        let { data, httpStatus, status } = state

        try {
            const response: any = await HttpServices.httpGet(IDAM.ATTR_GROUPS)
            httpStatus = response.status

            if (httpStatus === 200) {
                const payload: any = response.data.payload
                data.groups = payload.groups

                status = 'fulfilled'
            } else {
                status = 'rejected'
            }
        } catch (error) {
            status = 'rejected'
            httpStatus = 500
        }

        setstate((prev: any) => ({
            ...prev,
            data,
            status,
            httpStatus,
        }))
    }

    const stageHandler = (tab: string = 'attributes') => {
        switch (tab) {
            case 'basic':
                return <AttributeIdentity
                    state={state}
                    setstate={setstate}
                    blurHandler={onInputBlur}
                    changeHandler={onChangeHandler}
                    listboxHandler={onListBoxHandler}
                />

            case 'configuration':
                return <AttributeConfiguration
                    state={state}
                    setstate={setstate}
                    blurHandler={onInputBlur}
                    changeHandler={onChangeHandler}
                    listboxHandler={onListBoxHandler}
                />

            case 'oversight':
                return <AttributeOversight
                    state={state}
                    setstate={setstate}
                    blurHandler={onInputBlur}
                    changeHandler={onChangeHandler}
                    listboxHandler={onListBoxHandler}
                />

            default:
                return null
        }
    }

    const toggleModal = (key: ModalKeys, value: boolean, uuid?: any) => {
        setstate((prevState: any) => ({
            ...prevState,
            show: {
                ...prevState.show,
                [key]: value,
                uuid: uuid,
            },
        }))
    }

    const findSection = (fieldName: string) => {
        // Find section in which input belongs
        for (const [section, fields] of Object.entries(formSections)) {
            if (fields.includes(fieldName)) {
                return section;
            }
        }
        return null;
    }

    const sectionHasErrors = (section: string, errors: Record<string, string>) => {
        const fields = formSections[section] || [];
        // If any field still has an error
        return fields.some((field) => errors[field]);
    }

    const onListBoxHandler = (e: any, list: any) => {
        let { posting, data, errors, input, show } = state
        if (posting) return

        let updatedErrors = {
            ...errors
        }

        switch (list) {
            case 'attr_group':
                if (e === '__create_new__') {
                    // Show create new attribute group modal
                    toggleModal('createAttributeGroup', true)
                    return
                }

                const attrGroup = data.groups.find(
                    (grp: any) => grp.g === e
                )

                updatedErrors = {
                    ...updatedErrors,
                    attr_group: '',
                }

                setstate((prev: any) => ({
                    ...prev,
                    input: {
                        ...prev.input,
                        attr_group: attrGroup,
                    },
                    errors: updatedErrors
                }));
                break

            case 'format':
                const isSameFormat = input.format?.key === e.key;
                const isFreeText = e.key === 'freetext';
                const isPredefined = e.key === 'predefined';

                // Pre-calculate new values
                const multiline = isFreeText ? input.multiline : false;
                const displayStyle = isPredefined ? input.display_style : { key: "radio", label: "Radio Buttons" };
                const textCase = isFreeText
                    ? input.text_case
                    : { key: 'mixed', label: 'Mixed Case' };

                const minimum = isSameFormat ? input.minimum : '';
                const maximum = isSameFormat ? input.maximum : '';

                // Reset errors if format changes
                if (!isSameFormat) {
                    updatedErrors = {
                        ...updatedErrors,
                        format: '',
                        minimum: '',
                        maximum: '',
                        unit_label: '',
                        display_style: '',
                    }
                }

                setstate((prev: any) => ({
                    ...prev,
                    input: {
                        ...prev.input,
                        format: e,
                        minimum,
                        maximum,
                        multiline,
                        display_style: displayStyle,
                        whole_numbers: false,
                        allow_negative: false,
                        allow_special: false,
                        text_case: textCase,
                    },
                    show: {
                        ...prev.show,
                        minMaxVisibility: isSameFormat ? show.minMaxVisibility : false,
                    },
                    errors: updatedErrors,
                }))
                break

            case 'display_style':
                if (input.format?.key !== 'predefined') {
                    setstate((prev: any) => ({
                        ...prev,
                        input: {
                            ...prev.input,
                            display_style: { key: "radio", label: "Radio Buttons" },
                        },
                        errors: updatedErrors
                    }));
                } else {
                    updatedErrors = {
                        ...updatedErrors,
                        display_style: '',
                    }

                    setstate((prev: any) => ({
                        ...prev,
                        input: {
                            ...prev.input,
                            display_style: e,
                            allow_mulitple: e?.key !== 'dropdown' ? false : input.allow_mulitple,
                        },
                        errors: updatedErrors
                    }));
                }
                break;

            case 'text_case':
                if (input.format?.key !== 'freetext') {
                    setstate((prev: any) => ({
                        ...prev,
                        input: {
                            ...prev.input,
                            text_case: {
                                key: "mixed",
                                label: "Mixed Case",
                            },
                        },
                        errors: updatedErrors
                    }));
                } else {
                    setstate((prev: any) => ({
                        ...prev,
                        input: {
                            ...prev.input,
                            text_case: e,
                        },
                        errors: updatedErrors
                    }));
                }
                break
        }

        const sectionWithError = findSection(list)
        const stillHasErrors = sectionHasErrors(sectionWithError, updatedErrors)

        updatedErrors = {
            ...updatedErrors,
            [sectionWithError]: stillHasErrors ? updatedErrors[sectionWithError] : '',
        }

        setstate((prev: any) => ({
            ...prev,
            errors: updatedErrors,
        }))
    }

    const onChangeHandler = (e: any) => {
        let { posting, input, errors } = state
        if (posting) return

        let output: any = G_onInputChangeHandler(e)

        const updatedInput = {
            ...input,
            [e.target.name]: output.value
        };

        const updatedErrors = {
            ...errors,
            [e.target.name]: output.error
        };

        setstate((prev: any) => ({
            ...prev,
            input: updatedInput,
            errors: updatedErrors,
        }));
    }

    const targetLength = (name: string) => {
        switch (name) {
            case 'unit_label':
                return {
                    min: 1,
                    max: 30
                };

            default:
                return {
                    min: 3,
                    max: 30
                };
        }
    }

    const onInputBlur = (e: any) => {
        let { posting, input, errors } = state
        if (posting) return

        const target0 = targetLength(e.target.name)
        const title = e.target.name === 'name' ? 'Attribute name' : ''

        let output: any = G_onInputBlurHandler(e, title, target0.min, target0.max)

        let updatedInput = {
            ...input,
        }

        let updatedErrors = {
            ...errors,
        };

        switch (e.target.name) {
            case 'minimum':
            case 'maximum':
                const format = input.format?.key

                if (!["freetext"].includes(format)) {
                    // TODO: Add validation for non-numeric and non-freetext
                    return
                }

                const targetName = e.target.name
                const targetValue = e.target.value

                if (format === 'freetext') {
                    const num = Number(targetValue);
                    const isWholeNumber = Number.isInteger(num);

                    if (!isWholeNumber) {
                        output.error = "Length must be a whole number."
                    } else {
                        // Remove the error message from blur handler
                        output.error = ''

                        if (targetName === "minimum") {
                            const newMin = num < minLength ? minLength
                                : num > maxLength ? maxLength
                                    : num

                            updatedInput = {
                                ...updatedInput,
                                minimum: newMin.toString(),
                                maximum:
                                    input.maximum && Number(input.maximum) < newMin
                                        ? ""
                                        : input.maximum,
                            };
                        }

                        if (targetName === "maximum") {
                            const newMax = num < minLength ? minLength
                                : num > maxLength ? maxLength
                                    : num

                            updatedInput = {
                                ...updatedInput,
                                maximum: newMax.toString(),
                                minimum:
                                    input.minimum && Number(input.minimum) > newMax
                                        ? minLength.toString()
                                        : input.minimum,
                            };
                        }
                    }
                } else if (format === 'numeric') {
                    const num = Number(targetValue);

                    // Whole number check
                    if (input.whole_numbers && !Number.isInteger(num)) {
                        output.error = "Value must be a whole number.";
                    } else if (!input.allow_negative && num < 0) {
                        // Negative number check
                        output.error = "Negative numbers are not allowed.";
                    } else {
                        // Clear error if passed checks
                        output.error = "";

                        // Update fields
                        if (targetName === "minimum") {
                            updatedInput = {
                                ...updatedInput,
                                minimum: num.toString(),
                                maximum:
                                    input.maximum && Number(input.maximum) < num
                                        ? "" // reset maximum if smaller than minimum
                                        : input.maximum,
                            };
                        }

                        if (targetName === "maximum") {
                            updatedInput = {
                                ...updatedInput,
                                maximum: num.toString(),
                                minimum:
                                    input.minimum && Number(input.minimum) > num
                                        ? "" // reset minimum if greater than maximum
                                        : input.minimum,
                            };
                        }
                    }

                }
                break;

            default:
                updatedInput = {
                    ...updatedInput,
                    [e.target.name]: output.value
                }
                break;
        }

        const sectionWithError = findSection(e.target.name)
        const stillHasErrors = sectionHasErrors(sectionWithError, errors)

        updatedErrors = {
            ...updatedErrors,
            [e.target.name]: output.error,
            [sectionWithError]: stillHasErrors ? updatedErrors[sectionWithError] : ''
        };

        setstate((prev: any) => ({
            ...prev,
            input: updatedInput,
            errors: updatedErrors,
        }));
    }

    const onCompleteActionHandler = () => {
        getAttributeGroups()
    }

    const onBackStepNavigation = () => {
        let { posting, stage } = state
        if (posting) return

        let backStage = 'basic'

        switch (stage) {
            case 'oversight':
                backStage = 'configuration'
                break;

            case 'configuration':
                backStage = 'basic'
                break;

            default:
                break;
        }

        setstate((prev: any) => ({
            ...prev,
            stage: backStage
        }));
    }

    const onCreateAttributeFormHandler = (e: any) => {
        e.preventDefault()
        let { posting, stage, input, show } = state
        if (posting) return

        let valid = true
        let inputKeys = null
        let errorKeys = null

        switch (stage) {
            case 'basic':
                if (!input.attr_group) {
                    setstate((prev: any) => ({
                        ...prev,
                        errors: {
                            ...prev.errors,
                            attr_group: 'Kindly select an attribute group',
                            basic: 'Kindly clear errors in section before proceeding',
                        },
                    }));

                    return
                }

                inputKeys = [
                    'name',
                    'hint',
                ]

                inputKeys.forEach((inputKey: any) => {
                    try {
                        let oTarget = e.target[inputKey]

                        let event: any = {
                            target: {
                                name: oTarget.name,
                                required: oTarget.required,
                                value: oTarget.value.trim(),
                            }
                        }

                        onInputBlur(event)
                    } catch (error) {
                        // console.log(error);
                        // Override error
                    }
                })

                let { errors } = state

                errorKeys = [
                    'name', 'hint'
                ]

                errorKeys.forEach((errorObject: any) => {
                    if (errors[errorObject].length > 0) {
                        valid = false
                    }
                })

                if (valid) {
                    setstate((prev: any) => ({
                        ...prev,
                        stage: 'configuration',
                    }))
                }
                return

            case 'configuration':
                if (!input.format) {
                    valid = false

                    setstate((prev: any) => ({
                        ...prev,
                        errors: {
                            ...prev.errors,
                            format: 'Kindly select a data format',
                            configuration: 'Kindly clear errors in section before proceeding',
                        },
                    }));

                    return
                }

                const format = input.format?.key
                let updatedErrors = state.errors

                if (format === 'freetext') {
                    if (show.minMaxVisibility) {
                        const minVal = Number(input.minimum);
                        const maxVal = Number(input.maximum);

                        const isMinValid = Number.isInteger(minVal);
                        const isMaxValid = Number.isInteger(maxVal);

                        if (input.minimum && !isMinValid) {
                            valid = false
                            updatedErrors.minimum = "Minimum length must be a whole number.";
                        }

                        if (input.maximum && !isMaxValid) {
                            valid = false
                            updatedErrors.maximum = "Maximum length must be a whole number.";
                        }

                        if (isMinValid && (minVal < minLength || minVal > maxLength)) {
                            valid = false
                            updatedErrors.minimum = `Minimum must be between ${minLength} and ${maxLength}.`;
                        }

                        if (isMaxValid && (maxVal < minLength || maxVal > maxLength)) {
                            valid = false
                            updatedErrors.maximum = `Maximum must be between ${minLength} and ${maxLength}.`;
                        }

                        if (isMinValid && isMaxValid && minVal > maxVal) {
                            valid = false
                            updatedErrors.maximum = "Maximum must be greater than or equal to minimum.";
                        }
                    }
                } else if (format === 'numeric') {
                    if (show.minMaxVisibility) {
                        const minVal = Number(input.minimum);
                        const maxVal = Number(input.maximum);

                        // Require both minimum and maximum values
                        if (!input.minimum) {
                            valid = false;
                            updatedErrors.minimum = "Minimum value is required.";
                        }
                        if (!input.maximum) {
                            valid = false;
                            updatedErrors.maximum = "Maximum value is required.";
                        }

                        // Whole numbers check
                        if (input.whole_numbers) {
                            if (input.minimum && !Number.isInteger(minVal)) {
                                valid = false;
                                updatedErrors.minimum = "Minimum value must be a whole number.";
                            }
                            if (input.maximum && !Number.isInteger(maxVal)) {
                                valid = false;
                                updatedErrors.maximum = "Maximum value must be a whole number.";
                            }
                        }

                        // No negatives check
                        if (!input.allow_negative) {
                            if (input.minimum && minVal < 0) {
                                valid = false;
                                updatedErrors.minimum = "Minimum value cannot be negative.";
                            }
                            if (input.maximum && maxVal < 0) {
                                valid = false;
                                updatedErrors.maximum = "Maximum value cannot be negative.";
                            }
                        }

                        // Open range check
                        if (input.minimum && input.maximum && minVal > maxVal) {
                            valid = false;
                            updatedErrors.maximum = "Maximum must be greater than or equal to minimum.";
                        }
                    }
                } else if (format === 'predefined' && !input.display_style) {
                    valid = false
                    updatedErrors.display_style = 'Kindly select a data format'
                }

                if (format === 'numeric') {
                    inputKeys = [
                        'unit_label',
                    ]

                    inputKeys.forEach((inputKey: any) => {
                        try {
                            let oTarget = e.target[inputKey]

                            let event: any = {
                                target: {
                                    name: oTarget.name,
                                    required: oTarget.required,
                                    value: oTarget.value.trim(),
                                }
                            }

                            onInputBlur(event)
                        } catch (error) {
                            // console.log(error);
                            // Override error
                        }
                    })

                    let { errors } = state

                    errorKeys = [
                        'unit_label'
                    ]

                    errorKeys.forEach((errorObject: any) => {
                        if (errors[errorObject].length > 0) {
                            valid = false
                        }
                    })
                }

                if (!valid) {
                    updatedErrors.configuration = 'Kindly clear errors in section before proceeding'
                } else {
                    updatedErrors.configuration = ''
                }

                setstate((prev: any) => ({
                    ...prev,
                    stage: valid ? 'oversight' : 'configuration',
                    errors: updatedErrors
                }));
                return

            default:
                // Final stage proceed to post form
                if (valid) {
                    setstate((prev: any) => ({
                        ...prev,
                        posting: true,
                    }))

                    attributeDataHandler()
                }
                break;
        }
    }

    const attributeDataHandler = async () => {
        let { input, httpStatus } = state

        try {
            let formData = new FormData()
            formData.append('label', input.name)
            formData.append('attr_group', input.attr_group?.g)
            formData.append('hint', input.hint)

            formData.append('format', input.format?.key)
            formData.append('mandatory', input.mandatory ? "Y" : "N")
            formData.append('minimum', input.minimum)
            formData.append('maximum', input.maximum)
            formData.append('unit_label', input.unit_label)
            formData.append('multiline', input.multiline ? "Y" : "N")
            formData.append('whole_numbers', input.whole_numbers ? "Y" : "N")
            formData.append('allow_negative', input.allow_negative ? "Y" : "N")
            formData.append('allow_special', input.allow_special ? "Y" : "N")
            formData.append('allow_mulitple', input.allow_mulitple ? "Y" : "N")
            formData.append('display', input.display_style?.key)
            formData.append('case', input.text_case?.key)

            formData.append('ownership', input.ownership ? "Y" : "N")
            formData.append('coowners', input.coowners ? "Y" : "N")
            formData.append('restricted', input.restricted ? "Y" : "N")
            formData.append('editable', input.editable ? "Y" : "N")

            const response: any = await HttpServices.httpPost(IDAM.ATTR_DEFINITIONS, formData)
            httpStatus = response.status

            if (httpStatus === 200) {
                const payload: any = response.data.payload
                const globalId = payload.g

                const navigationState = {
                    previous_path: location.pathname,
                    title: input.name
                }

                let navigationRoute = getStandardRoute('USER.EDIT_ATTR_')

                navigationRoute = [":attribute"]
                    .reduce((route, param, index) =>
                        RouteHydration(route, param, [globalId][index]),
                        navigationRoute
                    );

                navigate(navigationRoute, { replace: false, state: navigationState });
                onCloseModal()
            } else {
                if (httpStatus === 422) {
                    // Map of server field names to frontend field names
                    const fieldMapping: Record<string, string> = {
                        label: 'name',
                        case: 'text_case',
                        display: 'display_style',
                    };

                    Object.entries(response.data).forEach(([serverField, errorMessages]) => {
                        const frontendField = fieldMapping[serverField] || serverField; // default to original if no mapping

                        setstate((prev: any) => ({
                            ...prev,
                            errors: {
                                ...prev.errors,
                                [frontendField]: errorMessages
                            }
                        }));
                    });
                } else {
                    toast.error(APPLICATION.ERR_MSG);
                }
            }
        } catch (error) {
            console.log(error);

            toast.error(APPLICATION.ERR_MSG);
        }

        setstate((prev: any) => ({
            ...prev,
            posting: false,
            httpStatus
        }));
    }

    return (
        <React.Fragment>
            <TwoToneModal
                show={show}
                status={state.status}
                posting={state.posting}
                showOrHide={onCloseModal}
                onSubmit={onCreateAttributeFormHandler}
                formAction={
                    state.stage === 'oversight' ? "Create" : "Next"
                }
                size={
                    state.status === 'fulfilled' ? "3xl"
                        : state.status === 'rejected' ? "md"
                            : "sm"
                }
                header={
                    <>
                        <div className="w-full flex flex-col h-full gap-y-2.5 px-2">
                            <div className="flex md:pb-0 pb-2 md:pt-2 pt-4 flex-row-reverse align-middle items-center">
                                <button type="button" className="rounded-md md:hidden text-gray-800 dark:text-gray-200 hover:text-gray-800 focus:outline-none flex flex-row align-middle pr-3"
                                    onClick={onCloseModal}>
                                    <span className="fas fa-times border-none text-lg"></span>
                                    <span className="sr-only text-sm ml-3">Close panel</span>
                                </button>

                                <div className="flex-grow">
                                    <h2 className="md:text-3xl text-2xl font-medium tracking-wider text-orange-600 dark:text-white md:text-orange-600 dark:md:text-white">
                                        New <br className="hidden md:block" />Attribute
                                    </h2>
                                </div>
                            </div>

                            <div className="sm:col-span-8 flex-grow lg:col-span-7 mb-3 flex flex-col gap-y-3 text-primary dark:text-slate-100 md:text-slate-600 dark:md:text-slate-400">
                                <span className="block md:py-2 text-sm">
                                    Create user-based attributes to capture the data that matters.
                                </span>
                            </div>
                        </div>
                    </>
                }
                contents={
                    <div className="w-full h-full flex flex-col md:px-3 px-1.5 flex-1">
                        <div className="h-auto md:h-[640px] px-2">
                            {
                                stageHandler(state.stage)
                            }
                        </div>
                    </div>
                }
            />

            <CreateOrEditAttributeGroup
                cargo={state.cargo}
                onComplete={onCompleteActionHandler}
                show={state.show.createAttributeGroup}
                showOrHide={() => toggleModal('createAttributeGroup', false)}
            />
        </React.Fragment>
    )
}